/**
 * TodoInlineEditor - Toggles between Text and TextInput on tap.
 * Submits on return key or blur. Used for inline task title editing.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  type StyleProp,
  type TextStyle,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Colors } from '@/constants/theme';
import { useTodoScroll } from './TodoScrollContext';

type TodoInlineEditorProps = {
  value: string;
  onSubmit: (newValue: string) => void;
  onEditingChange?: (isEditing: boolean) => void;
  style?: StyleProp<TextStyle>;
  palette: typeof Colors.light;
  /** When false, title renders as plain text — tap falls through to parent */
  editable?: boolean;
};

export function TodoInlineEditor({
  value,
  onSubmit,
  onEditingChange,
  style,
  palette,
  editable = true,
}: TodoInlineEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<TextInput>(null);
  const containerRef = useRef<View>(null);
  const { scrollToInput, setIsEditing } = useTodoScroll();

  const updateEditing = useCallback((isEditing: boolean) => {
    setEditing(isEditing);
    setIsEditing(isEditing);
    onEditingChange?.(isEditing);
  }, [onEditingChange, setIsEditing]);

  const handlePress = useCallback(() => {
    setDraft(value);
    updateEditing(true);
    // Focus on next tick after render, then scroll into view
    setTimeout(() => {
      inputRef.current?.focus();
      scrollToInput(containerRef);
    }, 50);
  }, [value, updateEditing, scrollToInput]);

  const handleSubmit = useCallback(() => {
    const trimmed = draft.trim();
    updateEditing(false);
    if (trimmed && trimmed !== value) {
      onSubmit(trimmed);
    }
  }, [draft, value, onSubmit, updateEditing]);

  if (editing) {
    return (
      <View ref={containerRef}>
        <TextInput
          ref={inputRef}
          style={[style, styles.input]}
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={handleSubmit}
          onBlur={handleSubmit}
          returnKeyType="done"
          blurOnSubmit
          autoFocus
        />
      </View>
    );
  }

  if (!editable) {
    return (
      <Text style={style} numberOfLines={2}>
        {value}
      </Text>
    );
  }

  return (
    <Pressable onPress={handlePress} style={styles.pressable}>
      <Text style={style} numberOfLines={2}>
        {value}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    padding: 0,
    margin: 0,
  },
});
